import React, { useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { 
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Typography
} from '@mui/material';
import SupportIcon from '@mui/icons-material/Support';
import UserContext from '../../../contexts/UserContext';
import ProjectContext from '../../../contexts/ProjectContext';
import {supportTheme} from '../../../theme';

import SupportDialogForm from './SupportDialogForm';
import SupportDialogSubmitting from './SupportDialogSubmitting';
import SupportDialogSuccess from './SupportDialogSuccess';
import SupportDialogError from './SupportDialogError';
import { useFetch } from '../../auth/useFetch';

import {
    DialogShell,
    DialogTextField,
    DialogHeader,
    DialogFooter
} from './SupportDialogStyles';



export default function SupportDialog(props){
    const { open, setOpen, locationPathname } = props
    const t = supportTheme
    const { konsolUser, konsolUserEmail } = useContext(UserContext)
    const projectContext = useContext(ProjectContext)

    const [dialogState, setDialogState] = useState('form') // 'form', 'submitting', 'success', 'error'
    const fetchWithAuth = useFetch()
    const [ticketKey, setTicketKey] = useState("")
    const [ticketURL, setTicketURL] = useState("")  
    const [errorMessage, setErrorMessage] = useState(null)
    const [successWarnings, setSuccessWarnings] = useState([])


    const [ticketStatus, setTicketStatus] = useState('Creating ticket');
    const [submitProgress, setSubmitProgress] = useState(0);

    const [formState, setFormState] = useState({
        subject: '',
        description: '',
        requestType: 'support',
        impact: 'medium',
        files: [],
        location: locationPathname,
    })

    useEffect(() => {
        setFormState((prev) => ({ ...prev, location: locationPathname }));
    }, [locationPathname]);


    useEffect(() => {
        setFormState((prevState) => ({
            ...prevState,
            userEmail: konsolUserEmail
        }));
    }, [konsolUserEmail]);


    // Separate clickAway and handleClose - we want to keep form data if the user clicks outside the dialog, but reset the form if they click the cancel button
    //   This will protect against user frustration if they click outside of the dialog by accident or to get data from the frontend
    const clickAway = () => {
        if (dialogState !== 'submitting'){
            setOpen(false);
        }
    }

    const handleClose = () => {
        setOpen(false);
        handleFormReset();
        setDialogState('form');
    };

    const handleFormReset = () => {
        setDialogState('form');
        setFormState({
            subject: '',
            description: '',
            requestType: 'support',
            impact: 'medium',
            files: [],
            location: locationPathname,
            userEmail: konsolUserEmail,
        })
    }
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const handleSubmit = async () => { 
        let currentProgress = 0
        setDialogState('submitting');
        try {
            currentProgress = 10
            setSubmitProgress(currentProgress);
            const { ticket_id, ticket_url } = await handleCreateTicket()
            // setTicketKey(ticket_id)
            // setTicketURL(ticket_url)
            currentProgress = 25
            setSubmitProgress(currentProgress);
            if (formState?.files && formState.files.length > 0) {
                await handleUploadAttachments(formState.files, ticket_id, currentProgress, 90)
            }
            currentProgress = 100
            setSubmitProgress(currentProgress);
            await sleep(500);
            setDialogState('success');
        }catch (error) {
            // TODO: might be good to handle partially successful submissions. Eg, ticket creation success but attachment upload failure. Invite user to upload via the 
            //   jira web interface instead?
            //   ALSO can we link to their ticket?
            setSubmitProgress(0);
            setDialogState('error')
            
            setErrorMessage(error.message)
        }
    }

    const handleCreateTicket = async () => {
        setTicketStatus('Creating ticket...')
        let formToSubmit = {...formState}
        delete formToSubmit.files
   
        // Add extra items to formToSubmit
        //   These are decoupled from formstate so that there's no chance of them persisting across users/projects
        formToSubmit.project = projectContext?.project_name ?? ''
        delete formToSubmit.userEmail
        var requestOptions = {
            method: 'POST',
            body: JSON.stringify(formToSubmit),
            headers: {
                'X-Project-ID': projectContext?.project_db,
                'Content-Type': 'application/json'
            },
        }

        let res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/support/ticket/`, requestOptions);
        let res_json = await res.json(); 
        
        if (res.status !== 200) {
            throw new Error(res_json?.message || "An error occurred while creating the ticket.");
        }

        let ticket_id = res_json?.ticket_id || null;
        if (!ticket_id) {
            throw new Error("Ticket ID not returned from the server.");
        }
        
        let ticket_url = res_json?.ticket_url || null;
        if (!ticket_url) {
            throw new Error("Ticket URL not returned from the server.");
        }
        setTicketURL(ticket_url);
        setTicketKey(ticket_id);

        return { ticket_id, ticket_url }
    }

    const handleUploadAttachments = async (files, ticketID, currentProgress) => {
        // For each file to upload, fire an upload request        
        setTicketStatus('Uploading attachments...')

        // Regardless of where this upload function is called in the ticket creation, always calculate progress based on the current progress and the number of files to upload.        
        let url = `${process.env.REACT_APP_BACKEND_URL}/konsol/support/ticket/${ticketID}/attachment/`

        const formData = new FormData();

        for (let i = 0; i < files.length; i++) {
            let file = files[i];          
            formData.append('file', file);          
        }

        setSubmitProgress(60)

        var requestOptions = {
            method: 'POST',
            body: formData,
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
        }

        let res = await fetchWithAuth(url, requestOptions);   
        
        if (res.status !== 200) {
            setSuccessWarnings(['attachment']);
        }

        setSubmitProgress(90)
        return true
    }
  
      

    const renderBody = () => {
        switch (dialogState) {
            case 'submitting':
                return <SupportDialogSubmitting formState={formState} setFormState={setFormState} ticketStatus={ticketStatus} submitProgress={submitProgress} />;
            case 'success':
                return <SupportDialogSuccess formState={formState} ticketKey={ticketKey} ticketURL={ticketURL} handleClose={handleClose} handleReturnToStart={handleFormReset} warnings={successWarnings} />;
            case 'error':
                return <SupportDialogError errorMessage={errorMessage} handleResubmitReturn={() => setDialogState('form')} handleClose={handleClose}/>;
            case 'form':
            default:
                return (
                    <SupportDialogForm formState={formState} setFormState={setFormState} handleSubmit={handleSubmit} handleClose={handleClose} />
                );
            }       
    };



    return (
       <React.Fragment>
            <DialogShell
                open={open}
                onClose={clickAway}
                maxWidth='lg'
                sx={{ boxSizing: 'border-box' }}
            >
                <DialogHeader>
                    <Box id='circle' 
                        sx={{ 
                            height: '40px', 
                            width: '40px', 
                            borderRadius: '50%', 
                            backgroundColor: 'rgb(45, 55, 45)',
                            border: '1px solid rgb(72, 94, 72)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <SupportIcon sx={{transform: 'rotate(45deg)', color: 'rgb(175, 255, 175)'}}/>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', pl: 2}}>
                        <Typography sx={{...t.type.title}}>Support</Typography>
                        <Typography sx={{...t.type.subtitle}}>Jira Service Management</Typography>
                    </Box>
                </DialogHeader>

                {renderBody()}

            </DialogShell>
        </React.Fragment>
    )
}